import React from 'react';
import { Analytics } from "@vercel/analytics/react";
import { Header, Footer } from "./LandingPage"; 
import SEO from './common/SEO'; 

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="flex min-h-screen flex-col relative">
      <SEO path="/privacy-policy" />
      <Header />
      <main className="flex-1 pt-24">
        <div className="container max-w-3xl mx-auto px-4">
          <div className="space-y-8">
            <div className="flex flex-col items-center space-y-4">
              <img 
                src="/currents-logo.png"
                alt="Currents Logo" 
                className="w-16 h-16 rounded-2xl shadow-lg"
              />
              <h1 className="text-3xl font-bold">Currents Privacy Policy</h1>
              <p className="text-sm text-gray-600">Last updated: 9 October 2024</p>
            </div>

            <section>
              <h2 className="text-2xl font-semibold mb-2">1. Introduction</h2>
              <p>Welcome to Currents. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application Currents.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-2">2. Information We Collect</h2>
              <p>We collect the following types of information:</p>
              <ul className="list-disc list-inside ml-4">
                <li>Personal information (name, email address)</li>
                <li>User-generated content, including:
                  <ul className="list-disc list-inside ml-8">
                    <li>Dive log data</li>
                    <li>Diving certifications</li>
                    <li>Comments and reviews</li>
                  </ul>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-2">3. How We Use Your Information</h2>
              <p>We use your information to:</p>
              <ul className="list-disc list-inside ml-4">
                <li>Provide and maintain our service</li>
                <li>Authenticate users and manage accounts</li>
                <li>Store and retrieve user-specific data, including:
                  <ul className="list-disc list-inside ml-8">
                    <li>Dive logs</li>
                    <li>Diving certifications</li>
                    <li>Diving gear inventory</li>
                  </ul>
                </li>
                <li>Improve and personalize user experience</li>
                <li>Communicate with you about updates and new features</li>
                <li>Analyze usage patterns to enhance our app</li>
              </ul>
              <p className="mt-4">Data Protection and Sharing:</p>
              <ul className="list-disc list-inside ml-4">
                <li>We regularly backup your data to prevent loss</li>
                <li>We do not share your personal information with third parties without your explicit consent, except as required by law</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-2">4. Data Storage and Security</h2>
              <p>Currents uses industry-standard cloud platforms, specifically Firebase and Supabase, to store and process your data securely. We implement various measures to protect your information, including:</p>
              <ul className="list-disc list-inside ml-4">
                <li>Secure user authentication and account management</li>
                <li>Encrypted data storage and transmission</li>
                <li>Strict access controls to your data</li>
                <li>Regular security updates and system maintenance</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-2">6. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc list-inside ml-4">
                <li>Access your personal information</li>
                <li>Correct inaccuracies in your data</li>
                <li>Request deletion of your account and associated data</li>
                <li>Opt-out of certain data collection and use</li>
                <li>Request a copy of your data in a portable format</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-2">7. Data Breaches</h2>
              <p>In the event of a data breach that affects your personal information, we will notify you in accordance with applicable laws and regulations.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-2">7. Changes to This Policy</h2>
              <p>We may update this policy from time to time. We will notify you of any significant changes by posting the new policy on this page and updating the &quot;Last updated&quot; date.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-2">8. Contact Us</h2>
              <p>If you have any questions about this Privacy Policy or how we handle your information, please contact us at: <a href="mailto:hello@deepsealabs.co" className="text-blue-600 hover:underline">hello@deepsealabs.co</a></p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
      <Analytics />
    </div>
  );
};

export default PrivacyPolicyPage;